// app/shared/providers/PostHogProvider.tsx
"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react";
import { Suspense, useEffect, useState } from "react";

import { useAuthHook } from "@/app/auth/services_and_hooks/useAuthHook";
import { posthogClientService } from "@/app/features/posthog/services_and_hooks/posthogClientService";

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Delay PostHog initialization to improve initial page load
    const timer = setTimeout(() => {
      // Initialize PostHog client service for both dev and production
      const apiHost =
        process.env.NODE_ENV === "development"
          ? "https://us.i.posthog.com" // Use cloud directly in dev to avoid 431 cookie-size errors with localhost proxy
          : "/ingest"; // Use proxy in production to bypass ad blockers

      posthogClientService.init({
        apiKey: process.env.NEXT_PUBLIC_POSTHOG_KEY!,
        apiHost: apiHost,
        uiHost: "https://us.posthog.com",
        capturePageview: false, // We'll handle pageviews manually
        capturePageleave: true,
        captureExceptions: true,
        debug: false, // Disable debug logging
      });

      // ✅ Forward PostHog events to GTM's dataLayer for pixel routing
      // Set up event forwarding after initialization
      if (typeof window !== "undefined") {
        const posthog = posthogClientService.getInstance();
        if (posthog) {
          posthog.on("eventCaptured", (eventName, properties) => {
            // Push PostHog events to GTM's dataLayer
            if ((window as any).dataLayer) {
              (window as any).dataLayer.push({
                event: eventName,
                ...properties,
              });
              console.log("[PostHog → GTM] Event forwarded:", eventName);
            }
          });
        }
      }

      setIsInitialized(true);
    }, 1000); // Delay 1 second

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    // Add cleanup for development to prevent errors when server shuts down
    // Only use visibilitychange to avoid blocking bfcache
    if (process.env.NODE_ENV === "development") {
      const handleVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
          // Stop tracking when page becomes hidden (dev server shutdown)
          // Use setTimeout to allow bfcache to work
          setTimeout(() => {
            posthogClientService.shutdown();
          }, 0);
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      };
    }
  }, [isInitialized]);

  // Now we use PostHog in both development AND production
  if (process.env.NODE_ENV === "development") {
    // In development, just render children without PHProvider wrapper since we use the service directly
    return <>{children}</>;
  }

  return (
    <PHProvider
      apiKey={process.env.NEXT_PUBLIC_POSTHOG_KEY!}
      options={{
        api_host: "https://us.posthog.com", // Use PostHog cloud instead of localhost
        ui_host: "https://us.posthog.com",
        capture_pageview: false, // We'll handle pageviews manually
        capture_pageleave: true,
        capture_exceptions: true,
        debug: false, // Disable debug logging
        disable_session_recording: true, // Disable session recording to prevent snapshots
        // ✅ Forward PostHog events to GTM's dataLayer for pixel routing
        loaded: (posthog) => {
          posthog.on("eventCaptured", (eventName, properties) => {
            // Push PostHog events to GTM's dataLayer
            if (typeof window !== "undefined" && (window as any).dataLayer) {
              (window as any).dataLayer.push({
                event: eventName,
                ...properties,
              });
              console.log("[PostHog → GTM] Event forwarded:", eventName);
            }
          });
        },
      }}
    >
      <Suspense fallback={null}>
        <PostHogPageView />
      </Suspense>
      {children}
    </PHProvider>
  );
}

function PostHogPageView(): null {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();

  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname;
      if (searchParams && searchParams.toString()) {
        url = url + `?${searchParams.toString()}`;
      }
      posthog.capture("$pageview", {
        $current_url: url,
      });
    }
  }, [pathname, searchParams, posthog]);

  return null;
}

// Export this component to be used in auth contexts
export function PostHogUserIdentifier(): null {
  const { user, isAuthenticated } = useAuthHook();

  useEffect(() => {
    if (isAuthenticated && user) {
      // Identify the user in PostHog
      posthogClientService.identify(user.id, {
        email: user.email || "",
        user_id: user.id,
      });
    }
  }, [isAuthenticated, user]);

  return null;
}
